import type { FocusGroupWidget } from '@inference.engine/widgets/web';
import { useTranslation } from 'react-i18next';

const MAX_PREVIEW_INSIGHTS = 2;

interface Props {
  data: FocusGroupWidget;
}

/**
 * Store-free, prop-only Library thumbnail for a focus-group artifact. The saved
 * artifact carries a self-contained snapshot in its `data` (title,
 * executiveSummary, personaSummaries), so the thumbnail renders straight from
 * `props.data` and keeps working even if the source focus group is later
 * removed. Shows the research title, a couple of executive-summary key
 * insights, and the participant count.
 */
export const FocusGroupThumbnail = (props: Props) => {
  const { t } = useTranslation('audiences');
  const { t: tCommon } = useTranslation('common');
  const data = props.data;
  const title = data.title || tCommon('artifact.artifactKindPalette.focusGroup');
  const insights = (data.executiveSummary?.keyInsights ?? []).slice(0, MAX_PREVIEW_INSIGHTS);
  const participantCount = data.personaSummaries?.length ?? 0;

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      <p className="line-clamp-2 text-sm font-semibold text-[#1A1A1A]">{title}</p>
      {participantCount > 0 && (
        <p className="text-xs text-[#6B6B6F]">
          {t('focusGroupWidget.focusGroupThumbnail.participantCount', {
            count: participantCount,
          })}
        </p>
      )}
      {insights.length > 0 && (
        <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-hidden">
          {insights.map((insight, index) => (
            <li key={index} className="line-clamp-2 text-[10px] leading-snug text-[#6B6B6F]">
              {insight}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
